import type { Language } from '@offer-catalog/services/i18n'
import type { ComponentType, ReactElement } from 'react'
import { VpFlagUnknownAsset } from '@vtmn-play/assets/react'
import { VpLoader } from '@vtmn-play/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCountryName } from '@/utils/countries'
import './flag-renderer.scss'

export interface FlagRendererProps {
  countryCode: string
  ariaHidden?: boolean
}

interface FlagState {
  element: ReactElement
  code: string
}

async function resolveFlagAsset(countryCode: string): Promise<ComponentType> {
  const module = await import('@vtmn-play/assets/react')
  const exportName = `VpFlag${countryCode.charAt(0).toUpperCase()}${countryCode.slice(1).toLowerCase()}Asset`
  if (exportName in module) {
    return module[exportName as keyof typeof module] as ComponentType
  }
  return VpFlagUnknownAsset
}

export function FlagRenderer({ countryCode, ariaHidden }: Readonly<FlagRendererProps>) {
  const { i18n, t } = useTranslation()
  const currentLanguage = i18n.language.split('-')[0] as Language
  const [flagState, setFlagState] = useState<FlagState | null>(null)

  useEffect(() => {
    let cancelled = false

    if (!countryCode) {
      return
    }

    void resolveFlagAsset(countryCode).then((Asset) => {
      if (!cancelled) {
        setFlagState({ element: <Asset />, code: countryCode })
      }
    })

    return () => {
      cancelled = true
    }
  }, [countryCode])

  const isResolved = flagState?.code === countryCode
  const countryName = getCountryName(countryCode, currentLanguage) ?? t('aria-label.unknown-country')
  let flagElement: ReactElement = <VpLoader />

  if (!countryCode) {
    flagElement = <VpFlagUnknownAsset />
  }
  else if (isResolved && flagState) {
    flagElement = flagState.element
  }

  return (
    <span
      className="flag-container"
      aria-hidden={ariaHidden || undefined}
      role={ariaHidden ? undefined : 'img'}
      aria-label={ariaHidden ? undefined : countryName}
      title={countryName}
    >
      {flagElement}
    </span>
  )
}
